import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ServerlessGPUPlaygroundRun from './run';
import ServerlessGPUPlaygroundRequirements from './requirements';
import ServerlessGPUPlaygroundDeploy from './deploy';
import { Link, useSearchParams } from 'react-router-dom';
import ViewCredit from '@/pages/setting/payment/view-credit';
import { Button } from '@/components/ui/button';
import { BookText, Code2, AlertCircle } from 'lucide-react';
import { PATH_EXAMPLE } from '@/constants';
import { useGetUserBalance } from '@/service/payment/hook';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import PATH_PAGE from '@/router/path';
import { useQuickTopup } from '@/hooks/useQuickTopup';
import { QuickTopupDialog } from '@/components/quick-topup/QuickTopupDialog';

const ServerlessGPUPlayground = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'run';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // Credit balance check
  const { data: creditData, isLoading: isLoadingCredit } = useGetUserBalance();
  const totalAvailableCredit = creditData?.balance ?? 0;
  const hasInsufficientBalance = totalAvailableCredit < 0.5;
  const { isOpen, setIsOpen, openTopup, onSuccess } = useQuickTopup();

  return (
    <div className="container">
      <div className="flex justify-end my-2">
        <Link
          to={PATH_EXAMPLE}
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            variant={'outline'}
            startIcon={<Code2 className="h-4 w-4" />}
          >
            Examples
          </Button>
        </Link>
      </div>
      {/* Insufficient Balance Warning */}
      {!isLoadingCredit && hasInsufficientBalance && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Insufficient Credit Balance</AlertTitle>
          <AlertDescription className="mt-2">
            <p>
              Your current balance is {fCurrencyUSDExtended(totalAvailableCredit)}.
              Please top up your account to use the playground.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={openTopup}
            >
              Quick Top-up $5
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList variant={'outline'}>
          <TabsTrigger value="run" variant={'outline'}>
            Run
          </TabsTrigger>
          <TabsTrigger value="deploy" variant={'outline'}>
            Deploy
          </TabsTrigger>
          <TabsTrigger value="requirements" variant={'outline'}>
            Requirements
          </TabsTrigger>
        </TabsList>
        <TabsContent value="run">
          <ServerlessGPUPlaygroundRun />
        </TabsContent>
        <TabsContent value="deploy">
          <ServerlessGPUPlaygroundDeploy />
        </TabsContent>
        <TabsContent value="requirements">
          <ServerlessGPUPlaygroundRequirements />
        </TabsContent>
      </Tabs>
      <div className="mt-6 border-t pt-6">
        <ViewCredit isShowPaymentLink={true} />
      </div>

      <QuickTopupDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={onSuccess} />
    </div>
  );
};

export default ServerlessGPUPlayground;
